import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

// Header, drawer and mobile icons share one authoritative read per event.
const cartCountRequests = new WeakMap();
const historyRestoreEvent = new Event('cart:history-restore');

/** @param {Event} event */
function readCartCount(event) {
  if (!cartCountRequests.has(event)) {
    const request = fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to reconcile cart count');
        return response.json();
      })
      .then((cart) => (Number.isInteger(cart.item_count) && cart.item_count >= 0 ? cart.item_count : null))
      // Keep the last confirmed count on a read failure; never assume the requested add succeeded.
      .catch(() => null)
      .finally(() => cartCountRequests.delete(event));
    cartCountRequests.set(event, request);
  }
  return cartCountRequests.get(event);
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];
  #countRevision = 0;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    // Fresh Liquid/section markup is authoritative, not a previous page's session count.
    // History can reuse stale HTML without bfcache (pageshow.persisted is then false).
    // Do this on connection too: a late module may have missed pageshow entirely.
    if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
      this.ensureCartBubbleIsCorrect(historyRestoreEvent);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    this.#countRevision++;
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect(event);
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const revision = ++this.#countRevision;
    const itemCount = event.detail.resource?.item_count;
    if (Number.isInteger(itemCount) && itemCount >= 0) {
      this.renderCartBubble(itemCount);
      return;
    }

    // Add responses (including partial/error adds) do not contain a total cart count.
    // Requested quantity is not an authoritative delta, even after a successful add.
    const confirmedCount = await readCartCount(event);
    if (revision === this.#countRevision && this.isConnected && confirmedCount !== null) {
      this.renderCartBubble(confirmedCount, !event.detail.data?.didError);
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} [animate] - Whether to animate the confirmed count.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Reconciles a page restored from history, including the back/forward cache.
   * @param {Event} event - Shared by restored icons; only in-flight reads are reused.
   */
  ensureCartBubbleIsCorrect = async (event) => {
    const revision = ++this.#countRevision;
    const itemCount = await readCartCount(event);
    if (revision === this.#countRevision && this.isConnected && itemCount !== null) {
      this.renderCartBubble(itemCount, false);
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
